import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';import axios from 'axios';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import DailyJournal from './pages/DailyJournal';
import WeeklyCheckin from './pages/WeeklyCheckin';
import DiscipleshipPath from './pages/DiscipleshipPath';
import AdminDashboard from './pages/AdminDashboard';
import AdminMessaging from './pages/AdminMessaging';
import Messages from './pages/Messages';
import ContactAdmin from './pages/ContactAdmin';
import NotificationSettings from './pages/NotificationSettings';
import UpgradeAdmin from './pages/UpgradeAdmin';
import Gamification from './pages/Gamification';
import SermonNotes from './pages/SermonNotes';
import PrayerWall from './pages/PrayerWall';
import PrayerGroup from './pages/PrayerGroup';
import Favorites from './pages/Favorites';

import Livestream from './pages/Livestream';
import Giving from './pages/Giving';
import Resources from './pages/Resources';
import AppTutorials from './pages/AppTutorials';
import ReflectionsArchive from './pages/ReflectionsArchive';
import DailyDevotionals from './pages/DailyDevotionals';
import ForgotPassword from './pages/ForgotPassword';
import SecurityQuestionsSetup from './pages/SecurityQuestionsSetup';
import { Toaster } from './components/ui/sonner';
import { useLocation } from 'react-router-dom';
import { registerServiceWorker } from './utils/notifications';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext(null);

// Protected Route Component. Also enforces the "security questions required
// before entering the app" gate for both new signups and legacy accounts.
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const [gateChecked, setGateChecked] = useState(
    localStorage.getItem('has_security_questions') !== null
  );

  useEffect(() => {
    // Legacy sessions won't have this key yet — bootstrap once from the API.
    if (!token) return;
    if (localStorage.getItem('has_security_questions') !== null) return;
    let cancelled = false;
    axios
      .get(`${API}/auth/security-questions/status`)
      .then((res) => {
        if (cancelled) return;
        const value = res.data?.has_security_questions ? 'true' : 'false';
        localStorage.setItem('has_security_questions', value);
        setGateChecked(true);
      })
      .catch(() => {
        if (!cancelled) setGateChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!token) {
    return <Navigate to="/" replace />;
  }
  if (!gateChecked) {
    // Silent bootstrap in progress — render nothing to avoid flicker.
    return null;
  }
  const hasSQ = localStorage.getItem('has_security_questions');
  if (hasSQ === 'false' && location.pathname !== '/security-questions') {
    return <Navigate to="/security-questions" replace />;
  }
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  
  if (!token) {
    return <Navigate to="/" replace />;
  }
  
  const user = userStr ? JSON.parse(userStr) : null;
  // Allow full admins AND prayer moderators; AdminDashboard itself gates individual tabs.
  const isAllowed = user && (user.role === 'admin' || user.is_prayer_moderator);
  if (!isAllowed) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  useEffect(() => {
    // Check if user is on the correct domain
    const currentHost = window.location.hostname;
    const expectedHost = new URL(BACKEND_URL).hostname;
    
    // If user is on a different domain (old PWA), show update banner
    if (currentHost !== expectedHost && currentHost !== 'localhost') {
      setShowUpdateBanner(true);
      console.warn('App is running from incorrect domain. Expected:', expectedHost, 'Got:', currentHost);
    }
    
    // Force service worker update check
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.update();
        });
      });
    }
    
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    // Setup axios interceptor to handle token expiration
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // If we get a 401 error (unauthorized), the token is expired or invalid
        if (error.response?.status === 401) {
          // Clear auth data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          
          // Redirect to login page
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );
    
    // Register service worker for PWA and notifications
    if ('serviceWorker' in navigator) {
      registerServiceWorker().catch(err => console.error('SW registration failed:', err));
    }
    
    setLoading(false);
    
    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = (token, userData, hasSecurityQuestions = null) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (hasSecurityQuestions !== null) {
      localStorage.setItem('has_security_questions', hasSecurityQuestions ? 'true' : 'false');
    } else {
      // Legacy callers didn't pass the flag; leave any existing value alone
      // so ProtectedRoute can bootstrap it from the API.
      localStorage.removeItem('has_security_questions');
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('has_security_questions');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  const handleUpdateApp = () => {
    // Clear caches and service workers
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }
    // Redirect to the correct URL
    window.location.href = BACKEND_URL.replace('/api', '').replace('https://', 'https://');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, API }}>
      <div className="App">
        {showUpdateBanner && (
          <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white p-4 z-50 shadow-lg">
            <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="font-medium text-sm sm:text-base">
                  App update available! Please update to get the latest features and fixes.
                </span>
              </div>
              <button
                onClick={handleUpdateApp}
                className="bg-white text-amber-600 px-4 py-2 rounded-lg font-semibold hover:bg-amber-50 transition-colors text-sm whitespace-nowrap"
              >
                Update Now
              </button>
            </div>
          </div>
        )}
        <BrowserRouter>
          <Routes>
            <Route 
              path="/" 
              element={user ? <Navigate to="/dashboard" /> : <LandingPage />} 
            />
            <Route 
              path="/forgot-password" 
              element={<ForgotPassword />} 
            />
            <Route 
              path="/security-questions" 
              element={
                <ProtectedRoute>
                  <SecurityQuestionsSetup />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tutorials"
              element={
                <ProtectedRoute>
                  <AppTutorials />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reflections"
              element={
                <ProtectedRoute>
                  <ReflectionsArchive />
                </ProtectedRoute>
              }
            />
            <Route
              path="/journal"
              element={
                <ProtectedRoute>
                  <DailyJournal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkin"
              element={
                <ProtectedRoute>
                  <WeeklyCheckin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discipleship"
              element={
                <ProtectedRoute>
                  <DiscipleshipPath />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/messages"
              element={
                <ProtectedRoute>
                  <AdminMessaging />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upgrade-admin"
              element={
                <ProtectedRoute>
                  <UpgradeAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact-admin"
              element={
                <ProtectedRoute>
                  <ContactAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gamification"
              element={
                <ProtectedRoute>
                  <Gamification />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sermon"
              element={
                <ProtectedRoute>
                  <SermonNotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/prayers"
              element={
                <ProtectedRoute>
                  <PrayerWall />
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/:id"
              element={
                <ProtectedRoute>
                  <PrayerGroup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/favorites"
              element={
                <ProtectedRoute>
                  <Favorites />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent-resources"
              element={<Navigate to="/resources?tab=parents" replace />}
            />
            <Route
              path="/livestream"
              element={
                <ProtectedRoute>
                  <Livestream />
                </ProtectedRoute>
              }
            />
            <Route
              path="/giving"
              element={
                <ProtectedRoute>
                  <Giving />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resources"
              element={
                <ProtectedRoute>
                  <Resources />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-devotionals"
              element={
                <ProtectedRoute>
                  <DailyDevotionals />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
